import { useEffect, useState } from "react";
import {
  activateRemoteWhatsAppConnection,
  createRemoteRetellConnection,
  createRemoteWhatsAppConnection,
  getRemoteRetellWebhookConfig,
  getRemoteWhatsAppWebhookConfig,
  listRemoteRetellConnections,
  listRemoteWhatsAppConnections,
  updateRemoteRetellConnection,
  updateRemoteWhatsAppConnection,
} from "../api";
import { Icon } from "../icons";
import type {
  AuthSession,
  RetellConnection,
  RetellWebhookConfig,
  WhatsAppConnection,
  WhatsAppWebhookConfig,
} from "../types";
import { WhatsAppGroupsPanel } from "./WhatsAppGroupsPanel";
import { TwilioWhatsAppPanel } from "./TwilioWhatsAppPanel";
import { LocalBridgeChannelsPanel } from "./LocalBridgeChannelsPanel";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

export function ChannelsView({
  organizationName,
  session,
  notify,
}: {
  organizationName: string;
  session: AuthSession;
  notify: Notify;
}) {
  const [connections, setConnections] = useState<WhatsAppConnection[]>([]);
  const [webhookConfig, setWebhookConfig] = useState<WhatsAppWebhookConfig | null>(null);
  const [retellConnections, setRetellConnections] = useState<RetellConnection[]>([]);
  const [retellConfig, setRetellConfig] = useState<RetellWebhookConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingConnectionId, setEditingConnectionId] = useState("");
  const [replacementToken, setReplacementToken] = useState("");
  const [form, setForm] = useState({
    label: `WhatsApp ${organizationName}`,
    wabaId: "",
    phoneNumberId: "",
    displayPhoneNumber: "",
    appId: "",
    appSecret: "",
    accessToken: "",
  });
  const [retellForm, setRetellForm] = useState({
    label: `Retell ${organizationName}`,
    businessPhone: "",
    whatsAppPhoneNumberId: "",
    apiKey: "",
  });

  useEffect(() => {
    setForm({ label: `WhatsApp ${organizationName}`, wabaId: "", phoneNumberId: "", displayPhoneNumber: "", appId: "", appSecret: "", accessToken: "" });
    setRetellForm({ label: `Retell ${organizationName}`, businessPhone: "", whatsAppPhoneNumberId: "", apiKey: "" });
    setEditingConnectionId("");
    setReplacementToken("");
  }, [organizationName]);

  useEffect(() => {
    let active = true;
    void load().catch((cause) => {
      if (active) notify("danger", "No se pudieron cargar los canales: " + errorMessage(cause));
    });
    return () => { active = false; };

    async function load() {
      const [items, config, calls, callConfig] = await Promise.all([
        listRemoteWhatsAppConnections(session),
        getRemoteWhatsAppWebhookConfig(session),
        listRemoteRetellConnections(session),
        getRemoteRetellWebhookConfig(session),
      ]);
      if (active) {
        setConnections(items);
        setWebhookConfig(config);
        setRetellConnections(calls);
        setRetellConfig(callConfig);
        setRetellForm((current) => ({
          ...current,
          whatsAppPhoneNumberId: current.whatsAppPhoneNumberId || items[0]?.phoneNumberId || "",
        }));
      }
    }
  }, [session, notify]);

  async function refreshWhatsApp() {
    setConnections(await listRemoteWhatsAppConnections(session));
  }

  async function refreshRetell() {
    setRetellConnections(await listRemoteRetellConnections(session));
  }

  async function createConnection() {
    setBusy(true);
    try {
      await createRemoteWhatsAppConnection(form, session);
      setForm((current) => ({
        ...current,
        wabaId: "",
        phoneNumberId: "",
        displayPhoneNumber: "",
        appId: "",
        appSecret: "",
        accessToken: "",
      }));
      await refreshWhatsApp();
      notify("success", "Número guardado. Ahora valida y activa la conexión con Meta.");
    } catch (cause) {
      notify("danger", "No se pudo guardar el número: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function createRetell() {
    setBusy(true);
    try {
      await createRemoteRetellConnection(retellForm, session);
      setRetellForm((current) => ({ ...current, apiKey: "" }));
      await refreshRetell();
      notify("success", "Retell conectado. Copia la URL del webhook en Retell para comenzar a recibir llamadas.");
    } catch (cause) {
      notify("danger", "No se pudo conectar Retell: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function activate(connection: WhatsAppConnection) {
    setBusy(true);
    try {
      await activateRemoteWhatsAppConnection(connection.connectionId, session);
      await refreshWhatsApp();
      notify("success", "Número validado y suscrito. Ya puede recibir eventos para esta organización.");
    } catch (cause) {
      await refreshWhatsApp().catch(() => undefined);
      notify("danger", "Meta no pudo validar la conexión: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function updateToken(connection: WhatsAppConnection) {
    const accessToken = replacementToken.trim();
    if (!accessToken) return;
    setBusy(true);
    try {
      await updateRemoteWhatsAppConnection(connection.connectionId, { accessToken }, session);
      await activateRemoteWhatsAppConnection(connection.connectionId, session);
      setEditingConnectionId("");
      setReplacementToken("");
      await refreshWhatsApp();
      notify("success", "Token actualizado y conexión validada. Ya puedes volver a responder por WhatsApp.");
    } catch (cause) {
      await refreshWhatsApp().catch(() => undefined);
      notify("danger", "No se pudo actualizar el token: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  function toggleTokenEditor(connectionId: string) {
    if (editingConnectionId === connectionId) {
      setEditingConnectionId("");
      setReplacementToken("");
      return;
    }
    setEditingConnectionId(connectionId);
    setReplacementToken("");
  }

  async function pause(connection: WhatsAppConnection) {
    setBusy(true);
    try {
      await updateRemoteWhatsAppConnection(connection.connectionId, { status: "paused" }, session);
      await refreshWhatsApp();
      notify("success", "Recepción pausada para este número.");
    } catch (cause) {
      notify("danger", "No se pudo pausar el número: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function toggleRetell(connection: RetellConnection) {
    setBusy(true);
    const status = connection.status === "active" ? "paused" : "active";
    try {
      await updateRemoteRetellConnection(connection.connectionId, { status }, session);
      await refreshRetell();
      notify("success", status === "active" ? "Recepción de llamadas Retell activada." : "Recepción de llamadas Retell pausada.");
    } catch (cause) {
      notify("danger", "No se pudo actualizar Retell: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const ready = form.label.trim() && form.wabaId.trim() && form.phoneNumberId.trim() &&
    form.appSecret.trim() && form.accessToken.trim();
  const retellReady = retellForm.label.trim() && retellForm.businessPhone.trim() && retellForm.apiKey.trim();

  return (
    <div className="standard-page admin-page">
      <section className="standard-page-header compact-page-header">
        <div>
          <p className="section-kicker">CANALES · {organizationName.toUpperCase()}</p>
          <h1>Canales conectados</h1>
          <p>Administra los números de WhatsApp, sus grupos oficiales y las llamadas de Retell de esta organización.</p>
        </div>
      </section>

      <LocalBridgeChannelsPanel organizationName={organizationName} session={session} notify={notify} />

      <section className="channel-section">
        <div className="channel-section-heading">
          <span className="connection-icon"><Icon name="whatsapp" size={20} /></span>
          <div><p className="section-kicker">MENSAJERÍA</p><h2>WhatsApp Business</h2></div>
        </div>

        {webhookConfig ? (
          <section className="card-panel webhook-config-panel">
            <div>
              <p className="section-kicker">CONFIGURACIÓN PARA META</p>
              <h2>Webhook de Conversa Smart</h2>
              <p>Usa estos dos valores en WhatsApp → Configuración dentro de la aplicación de Meta.</p>
            </div>
            <label className="field-label"><span>URL de devolución de llamada</span><code>{webhookConfig.callbackUrl}</code></label>
            <label className="field-label"><span>Token de verificación</span><code>{webhookConfig.verifyToken}</code></label>
            <p className="secret-note"><Icon name="check" size={15} /> Visible sólo para administradores de esta organización.</p>
          </section>
        ) : null}

        <div className="channel-admin-grid">
          <section className="card-panel admin-panel">
            <div className="panel-title-row">
              <div><p className="section-kicker">CONEXIONES</p><h2>Números de esta organización</h2></div>
              <span className="count-badge">{connections.length}</span>
            </div>
            <div className="connection-list">
              {connections.map((connection) => (
                <article className="connection-row" key={connection.connectionId}>
                  <span className="connection-icon"><Icon name="whatsapp" size={20} /></span>
                  <div>
                    <strong>{connection.label}</strong>
                    <small>{connection.displayPhoneNumber || "Número aún no verificado"} · ID {connection.phoneNumberId}</small>
                    <small>WABA {connection.wabaId}{connection.verifiedName ? " · " + connection.verifiedName : ""}</small>
                  </div>
                  <span className={"state-chip " + connection.status}>{statusLabel(connection.status)}</span>
                  <div className="connection-actions">
                    <button className="button soft compact" disabled={busy} onClick={() => toggleTokenEditor(connection.connectionId)} type="button">
                      {editingConnectionId === connection.connectionId ? "Cancelar edición" : "Actualizar token"}
                    </button>
                    {connection.status !== "active" ? (
                      <button className="button primary compact" disabled={busy} onClick={() => void activate(connection)} type="button">Validar y activar</button>
                    ) : (
                      <button className="button soft compact" disabled={busy} onClick={() => void pause(connection)} type="button">Pausar</button>
                    )}
                  </div>
                  {editingConnectionId === connection.connectionId ? (
                    <div className="credential-update-panel">
                      <label className="field-label">
                        <span>Nuevo System User Access Token</span>
                        <input type="password" autoComplete="new-password" value={replacementToken} onChange={(event) => setReplacementToken(event.target.value)} placeholder="Pega aquí el token permanente" />
                      </label>
                      <button className="button primary compact" disabled={busy || !replacementToken.trim()} onClick={() => void updateToken(connection)} type="button">{busy ? "Validando…" : "Guardar y validar"}</button>
                      <p className="secret-note"><Icon name="check" size={15} /> Sólo reemplaza el token. El App Secret y la configuración del número se conservan.</p>
                    </div>
                  ) : null}
                </article>
              ))}
              {!connections.length ? (
                <div className="friendly-empty"><span><Icon name="whatsapp" size={24} /></span><strong>Sin números conectados</strong><p>Agrega el WhatsApp de la organización para comenzar.</p></div>
              ) : null}
            </div>
          </section>

          <section className="card-panel admin-panel connection-form-panel">
            <div className="panel-title-row"><div><p className="section-kicker">NUEVO NÚMERO</p><h2>Agregar conexión</h2></div></div>
            <p className="form-help">Este bloque es para números oficiales de Meta. Los chips físicos que participan en grupos se preparan en “Chips para WhatsApp”.</p>
            <div className="form-stack">
              <label className="field-label"><span>Nombre visible</span><input value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} /></label>
              <label className="field-label"><span>WABA ID</span><input value={form.wabaId} onChange={(event) => setForm({ ...form, wabaId: event.target.value })} inputMode="numeric" /></label>
              <label className="field-label"><span>Phone Number ID</span><input value={form.phoneNumberId} onChange={(event) => setForm({ ...form, phoneNumberId: event.target.value })} inputMode="numeric" /></label>
              <label className="field-label"><span>Número visible opcional</span><input value={form.displayPhoneNumber} onChange={(event) => setForm({ ...form, displayPhoneNumber: event.target.value })} placeholder="+56 9..." /></label>
              <label className="field-label"><span>Meta App ID opcional</span><input value={form.appId} onChange={(event) => setForm({ ...form, appId: event.target.value })} /></label>
              <label className="field-label"><span>App Secret</span><input type="password" autoComplete="new-password" value={form.appSecret} onChange={(event) => setForm({ ...form, appSecret: event.target.value })} /></label>
              <label className="field-label"><span>System User Access Token</span><input type="password" autoComplete="new-password" value={form.accessToken} onChange={(event) => setForm({ ...form, accessToken: event.target.value })} /></label>
            </div>
            <p className="secret-note"><Icon name="check" size={15} /> Las credenciales se guardan cifradas en AWS Secrets Manager y nunca vuelven al navegador.</p>
            <button className="button primary" disabled={busy || !ready} onClick={() => void createConnection()} type="button"><Icon name="plus" size={16} /> Guardar número</button>
          </section>
        </div>
      </section>

      <WhatsAppGroupsPanel connections={connections} session={session} notify={notify} />

      <TwilioWhatsAppPanel organizationName={organizationName} session={session} notify={notify} />

      <section className="channel-section retell-channel-section">
        <div className="channel-section-heading">
          <span className="connection-icon call"><Icon name="clock" size={20} /></span>
          <div><p className="section-kicker">LLAMADAS</p><h2>Retell AI</h2></div>
        </div>

        {retellConfig ? (
          <section className="card-panel webhook-config-panel retell-webhook-panel">
            <div>
              <p className="section-kicker">CONFIGURACIÓN PARA RETELL</p>
              <h2>Webhook de llamadas</h2>
              <p>Pega esta URL en la configuración de webhook de Retell. Conversa Smart validará la firma y recibirá el transcript cuando termine el análisis.</p>
            </div>
            <label className="field-label retell-callback"><span>Webhook URL</span><code>{retellConfig.callbackUrl}</code></label>
            <p className="secret-note"><Icon name="check" size={15} /> El API key se guarda cifrado y se usa sólo para verificar que cada evento venga de Retell.</p>
          </section>
        ) : null}

        <div className="channel-admin-grid">
          <section className="card-panel admin-panel">
            <div className="panel-title-row">
              <div><p className="section-kicker">CONEXIONES</p><h2>Números atendidos por Retell</h2></div>
              <span className="count-badge">{retellConnections.length}</span>
            </div>
            <div className="connection-list">
              {retellConnections.map((connection) => (
                <article className="connection-row" key={connection.connectionId}>
                  <span className="connection-icon call"><Icon name="clock" size={20} /></span>
                  <div>
                    <strong>{connection.label}</strong>
                    <small>{connection.businessPhone}</small>
                    <small>{connection.whatsAppPhoneNumberId ? "Unido al WhatsApp ID " + connection.whatsAppPhoneNumberId : "Conversación de llamadas independiente"}</small>
                  </div>
                  <span className={"state-chip " + connection.status}>{connection.status === "active" ? "Activo" : "Pausado"}</span>
                  <div className="connection-actions">
                    <button className={connection.status === "active" ? "button soft compact" : "button primary compact"} disabled={busy} onClick={() => void toggleRetell(connection)} type="button">
                      {connection.status === "active" ? "Pausar" : "Activar"}
                    </button>
                  </div>
                </article>
              ))}
              {!retellConnections.length ? (
                <div className="friendly-empty"><span><Icon name="clock" size={24} /></span><strong>Retell aún no está conectado</strong><p>Agrega el número que hoy redirige las llamadas al agente de Retell.</p></div>
              ) : null}
            </div>
          </section>

          <section className="card-panel admin-panel connection-form-panel">
            <div className="panel-title-row"><div><p className="section-kicker">NUEVA CONEXIÓN</p><h2>Conectar Retell</h2></div></div>
            <p className="form-help">El número debe ser el mismo que recibe la llamada en Twilio. Al indicar el Phone Number ID de WhatsApp, llamadas y mensajes del mismo cliente quedan en una sola conversación.</p>
            <div className="form-stack">
              <label className="field-label"><span>Nombre de la conexión</span><input value={retellForm.label} onChange={(event) => setRetellForm({ ...retellForm, label: event.target.value })} /></label>
              <label className="field-label"><span>Número atendido en Twilio</span><input value={retellForm.businessPhone} onChange={(event) => setRetellForm({ ...retellForm, businessPhone: event.target.value })} placeholder="+56…" /></label>
              <label className="field-label"><span>Phone Number ID de WhatsApp opcional</span><input value={retellForm.whatsAppPhoneNumberId} onChange={(event) => setRetellForm({ ...retellForm, whatsAppPhoneNumberId: event.target.value })} inputMode="numeric" /><small>Permite unir llamada y chat del mismo contacto.</small></label>
              <label className="field-label"><span>Retell API key</span><input type="password" autoComplete="new-password" value={retellForm.apiKey} onChange={(event) => setRetellForm({ ...retellForm, apiKey: event.target.value })} placeholder="key_..." /></label>
            </div>
            <p className="secret-note"><Icon name="check" size={15} /> La clave se guarda cifrada en AWS Secrets Manager y nunca vuelve al navegador.</p>
            <button className="button primary" disabled={busy || !retellReady} onClick={() => void createRetell()} type="button"><Icon name="plus" size={16} /> Conectar Retell</button>
          </section>
        </div>
      </section>
    </div>
  );
}

function statusLabel(status: WhatsAppConnection["status"]) {
  if (status === "active") return "Activo";
  if (status === "paused") return "Pausado";
  if (status === "error") return "Revisar";
  return "Pendiente";
}

function errorMessage(cause: unknown) {
  const code = cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO";
  return code.replaceAll("_", " ").toLocaleLowerCase("es-CL");
}
